export type TrendPeriod = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';

export type DateRange = {
  from: Date;
  to: Date;
};

export type TrendPoint = {
  label: string;
  key: string;
  /** User acquisition (new users in bucket) */
  acquired: number;
  signups: number;
  logins: number;
  failedLogins: number;
  /** Staff sign-ins to ff-admin, already counted in logins / failedLogins */
  adminLogins: number;
  adminFailedLogins: number;
  sessions: number;
  churned: number;
  engagements: number;
  activeUsers: number;
  /** New listings created in bucket */
  listings: number;
  listingsNew: number;
  /** Active listings level at end of bucket */
  listingsActive: number;
  /** Signed-in members active per day, averaged over the bucket's days */
  dau: number;
  /** Members active in the 30 days up to the end of the bucket */
  mau: number;
  /** Members first seen in the range during this bucket */
  newActive: number;
  /** Visitors (members + anonymous) per day, averaged over the bucket's days */
  visitors: number;
  /** Visitors in the 30 days up to the end of the bucket */
  visitors30d: number;
  /** Visitors first seen in the range during this bucket */
  newVisitors: number;
  /** Active days by people never seen before / seen before, added up over the bucket */
  membersNew: number;
  membersReturning: number;
  visitorsNew: number;
  visitorsReturning: number;
  /** Marketplace orders paid, and their value in the main currency */
  ordersPaid: number;
  gmv: number;
  /** Platform payments in the main currency, by outcome */
  collected: number;
  payCollected: number;
  payPending: number;
  payCanceled: number;
  /** Messages received, answered, and the answers by how fast they came */
  inbound: number;
  replied: number;
  replyLt5m: number;
  replyLt30m: number;
  replyLt2h: number;
  replyLt12h: number;
  replyLt48h: number;
  replyGte48h: number;
  subsStarted: number;
  subsEnded: number;
  /** Subscriptions in force at end of bucket */
  subsActive: number;
  /** Of those, how many were past due; null where no daily snapshot recorded it */
  subsPastDue: number | null;
};

/** Counts that add up over time; everything else on a point is a level or an average. */
export const FLOW_FIELDS = [
  'acquired',
  'signups',
  'logins',
  'failedLogins',
  'adminLogins',
  'adminFailedLogins',
  'sessions',
  'churned',
  'engagements',
  'listings',
  'listingsNew',
  'newActive',
  'newVisitors',
  'membersNew',
  'membersReturning',
  'visitorsNew',
  'visitorsReturning',
  'ordersPaid',
  'gmv',
  'collected',
  'payCollected',
  'payPending',
  'payCanceled',
  'inbound',
  'replied',
  'replyLt5m',
  'replyLt30m',
  'replyLt2h',
  'replyLt12h',
  'replyLt48h',
  'replyGte48h',
  'subsStarted',
  'subsEnded',
] as const satisfies readonly (keyof TrendPoint)[];

export type MixItem = {
  key: string;
  label: string;
  count: number;
};

export type EngagementMixItem = MixItem;

export type OverviewKpis = {
  totalUsers: number;
  activeUsers: number;
  signups: number;
  logins: number;
  failedLogins: number;
  sessions: number;
  acquired: number;
  churnRate: number;
  inactiveUsers: number;
  activeListings: number;
  totalListings: number;
  newListings: number;
  engagements: number;
  orgs: number;
  dau: number;
  dauAvg: number;
  mau: number;
  dailyVisitors: number;
  monthlyVisitors: number;
  dauDelta: { pct: number; direction: 'up' | 'down' | 'flat' };
  signupDelta: { pct: number; direction: 'up' | 'down' | 'flat' };
  loginDelta: { pct: number; direction: 'up' | 'down' | 'flat' };
  failedLoginDelta: { pct: number; direction: 'up' | 'down' | 'flat' };
  acquiredDelta: { pct: number; direction: 'up' | 'down' | 'flat' };
  churnDelta: { pct: number; direction: 'up' | 'down' | 'flat' };
  engagementDelta: { pct: number; direction: 'up' | 'down' | 'flat' };
  usersDelta: { pct: number; direction: 'up' | 'down' | 'flat' };
  listingsDelta: { pct: number; direction: 'up' | 'down' | 'flat' };
};

const DAY_MS = 86_400_000;

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function startOfWeek(d: Date): Date {
  const day = startOfDay(d);
  day.setDate(day.getDate() - day.getDay());
  return day;
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function startOfQuarter(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() - (d.getMonth() % 3), 1);
}

function startOfYear(d: Date): Date {
  return new Date(d.getFullYear(), 0, 1);
}

const BUCKET_START: Record<TrendPeriod, (d: Date) => Date> = {
  daily: startOfDay,
  weekly: startOfWeek,
  monthly: startOfMonth,
  quarterly: startOfQuarter,
  yearly: startOfYear,
};

export function addPeriod(d: Date, period: TrendPeriod, n: number): Date {
  const next = new Date(d);
  if (period === 'daily') next.setDate(next.getDate() + n);
  else if (period === 'weekly') next.setDate(next.getDate() + n * 7);
  else if (period === 'monthly') next.setMonth(next.getMonth() + n);
  else if (period === 'quarterly') next.setMonth(next.getMonth() + n * 3);
  else next.setFullYear(next.getFullYear() + n);
  return next;
}

export function formatBucketLabel(period: TrendPeriod, start: Date): string {
  if (period === 'daily') {
    return start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
  if (period === 'weekly') {
    return `W ${start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
  }
  if (period === 'quarterly') {
    return `Q${Math.floor(start.getMonth() / 3) + 1} ${String(start.getFullYear()).slice(2)}`;
  }
  if (period === 'yearly') return String(start.getFullYear());
  return start.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
}

function hash(n: number): number {
  const x = Math.sin(n * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}

function scaleForPeriod(period: TrendPeriod) {
  if (period === 'daily') {
    return {
      acq: 18,
      churn: 6,
      eng: 420,
      active: 1180,
      listings: 38,
      logins: 640,
      failed: 42,
      sessions: 890,
    };
  }
  if (period === 'weekly') {
    return {
      acq: 95,
      churn: 28,
      eng: 2800,
      active: 1240,
      listings: 210,
      logins: 4200,
      failed: 210,
      sessions: 5600,
    };
  }
  return {
    acq: 380,
    churn: 95,
    eng: 11200,
    active: 1320,
    listings: 860,
    logins: 16800,
    failed: 780,
    sessions: 22100,
  };
}

/** Default window for a granularity when no custom range is set. */
export function defaultRangeForPeriod(period: TrendPeriod, now = new Date()): DateRange {
  const to = startOfDay(now);
  if (period === 'daily') {
    return { from: startOfDay(new Date(to.getTime() - 13 * DAY_MS)), to };
  }
  if (period === 'weekly') {
    return { from: startOfWeek(new Date(to.getTime() - 11 * 7 * DAY_MS)), to };
  }
  if (period === 'quarterly') {
    return { from: startOfQuarter(new Date(to.getFullYear(), to.getMonth() - 9, 1)), to };
  }
  if (period === 'yearly') return { from: new Date(to.getFullYear() - 2, 0, 1), to };
  return {
    from: startOfMonth(new Date(to.getFullYear(), to.getMonth() - 11, 1)),
    to,
  };
}

export function toInputDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function fromInputDate(value: string): Date | null {
  if (!value) return null;
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return null;
  const date = new Date(y, m - 1, d);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatRangeLabel(range: DateRange): string {
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
  return `${range.from.toLocaleDateString(undefined, opts)} – ${range.to.toLocaleDateString(undefined, opts)}`;
}

export type RangePreset = {
  id: string;
  label: string;
  getRange: (now?: Date) => DateRange;
};

export const RANGE_PRESETS: RangePreset[] = [
  {
    id: '7d',
    label: '7d',
    getRange: (now = new Date()) => {
      const to = startOfDay(now);
      return { from: startOfDay(new Date(to.getTime() - 6 * DAY_MS)), to };
    },
  },
  {
    id: '30d',
    label: '30d',
    getRange: (now = new Date()) => {
      const to = startOfDay(now);
      return { from: startOfDay(new Date(to.getTime() - 29 * DAY_MS)), to };
    },
  },
  {
    id: '90d',
    label: '90d',
    getRange: (now = new Date()) => {
      const to = startOfDay(now);
      return { from: startOfDay(new Date(to.getTime() - 89 * DAY_MS)), to };
    },
  },
  {
    id: '12m',
    label: '12m',
    getRange: (now = new Date()) => {
      const to = startOfDay(now);
      return {
        from: startOfMonth(new Date(to.getFullYear(), to.getMonth() - 11, 1)),
        to,
      };
    },
  },
];

function normalizeRange(range: DateRange, period?: TrendPeriod): DateRange {
  let from = startOfDay(range.from);
  let to = startOfDay(range.to);
  if (from > to) [from, to] = [to, from];
  // Cap span so charts stay readable; yearly buckets need more than one year.
  const maxDays = period === 'yearly' ? 3 * 366 : 366;
  if (to.getTime() - from.getTime() > maxDays * DAY_MS) {
    from = startOfDay(new Date(to.getTime() - maxDays * DAY_MS));
  }
  return { from, to };
}

export function buildBucketStarts(period: TrendPeriod, range: DateRange): Date[] {
  const { from, to } = normalizeRange(range, period);
  let cursor = BUCKET_START[period](from);
  const end = BUCKET_START[period](to);

  const starts: Date[] = [];
  const maxBuckets = period === 'daily' ? 62 : period === 'weekly' ? 52 : 24;

  while (cursor <= end && starts.length < maxBuckets) {
    starts.push(new Date(cursor));
    cursor = addPeriod(cursor, period, 1);
  }

  if (starts.length === 0) starts.push(startOfDay(to));
  return starts;
}

/** Deterministic mock time-series for a granularity + date range. */
export function getMockTrendSeries(
  period: TrendPeriod,
  range?: DateRange,
  now = new Date(),
): TrendPoint[] {
  const resolved = normalizeRange(range ?? defaultRangeForPeriod(period, now));
  const starts = buildBucketStarts(period, resolved);
  const scale = scaleForPeriod(period);
  const count = starts.length;
  const periodSeed = period === 'daily' ? 1 : period === 'weekly' ? 2 : 3;

  return starts.map((start, i) => {
    const t = i + 1;
    const growth = 0.88 + (t / Math.max(count, 1)) * 0.22;
    const wave = 0.85 + 0.3 * Math.sin(t * 0.85);
    const noise = 0.9 + hash(t * 17 + periodSeed + start.getDate()) * 0.2;
    const loginWave = 0.9 + 0.25 * Math.sin(t * 0.55 + 1.2);
    const signups = Math.max(1, Math.round(scale.acq * growth * wave * noise));
    // Acquisition slightly ahead of raw signups (includes invites / org seats)
    const acquired = Math.max(signups, Math.round(signups * (1.05 + hash(t + 3) * 0.1)));
    const listingsNew = Math.max(5, Math.round(scale.listings * growth * wave * noise));
    const listingsActive = Math.max(
      80,
      Math.round(
        (period === 'daily' ? 3200 : period === 'weekly' ? 3400 : 3600) *
          growth *
          (0.96 + noise * 0.06),
      ),
    );

    return {
      label: formatBucketLabel(period, start),
      key: start.toISOString().slice(0, 10),
      signups,
      acquired,
      logins: Math.max(10, Math.round(scale.logins * growth * loginWave * noise)),
      failedLogins: Math.max(1, Math.round(scale.failed * (1.1 - growth * 0.2) * noise)),
      sessions: Math.max(15, Math.round(scale.sessions * growth * loginWave * noise)),
      churned: Math.max(
        0,
        Math.round(scale.churn * (1.05 - growth * 0.15) * (1.1 - wave * 0.2) * noise),
      ),
      engagements: Math.max(10, Math.round(scale.eng * growth * wave * noise)),
      activeUsers: Math.max(50, Math.round(scale.active * growth * (0.95 + noise * 0.08))),
      listings: listingsNew,
      listingsNew,
      listingsActive,
      adminLogins: 0,
      adminFailedLogins: 0,
      dau: 0,
      mau: 0,
      newActive: 0,
      visitors: 0,
      visitors30d: 0,
      newVisitors: 0,
      membersNew: 0,
      membersReturning: 0,
      visitorsNew: 0,
      visitorsReturning: 0,
      ordersPaid: 0,
      gmv: 0,
      collected: 0,
      payCollected: 0,
      payPending: 0,
      payCanceled: 0,
      inbound: 0,
      replied: 0,
      replyLt5m: 0,
      replyLt30m: 0,
      replyLt2h: 0,
      replyLt12h: 0,
      replyLt48h: 0,
      replyGte48h: 0,
      subsStarted: 0,
      subsEnded: 0,
      subsActive: 0,
      subsPastDue: null,
    };
  });
}

export function getMockOverviewKpis(period: TrendPeriod, series: TrendPoint[]): OverviewKpis {
  const signups = sumField(series, 'signups');
  const acquired = sumField(series, 'acquired');
  const logins = sumField(series, 'logins');
  const failedLogins = sumField(series, 'failedLogins');
  const sessions = sumField(series, 'sessions');
  const engagements = sumField(series, 'engagements');
  const newListings = sumField(series, 'listingsNew');
  const half = Math.floor(series.length / 2);
  const recent = series.slice(half);
  const prior = series.slice(0, half);

  const totalUsers = period === 'daily' ? 12_480 : period === 'weekly' ? 12_640 : 13_210;
  const activeUsers = series[series.length - 1]?.activeUsers ?? 1_180;
  const inactiveUsers = Math.max(0, totalUsers - activeUsers);
  const churnRate = totalUsers > 0 ? inactiveUsers / totalUsers : 0;
  const activeListings = series[series.length - 1]?.listingsActive ?? 3_420;

  return {
    totalUsers,
    activeUsers,
    signups,
    logins,
    failedLogins,
    sessions,
    acquired,
    churnRate,
    inactiveUsers,
    activeListings,
    totalListings: Math.round(activeListings * 1.42),
    newListings,
    engagements,
    orgs: 286,
    dau: 0,
    dauAvg: 0,
    mau: 0,
    dailyVisitors: 0,
    monthlyVisitors: 0,
    dauDelta: { pct: 0, direction: 'flat' },
    signupDelta: periodDelta(sumField(recent, 'signups'), sumField(prior, 'signups')),
    loginDelta: periodDelta(sumField(recent, 'logins'), sumField(prior, 'logins')),
    failedLoginDelta: periodDelta(sumField(recent, 'failedLogins'), sumField(prior, 'failedLogins')),
    acquiredDelta: periodDelta(sumField(recent, 'acquired'), sumField(prior, 'acquired')),
    churnDelta: periodDelta(sumField(recent, 'churned'), sumField(prior, 'churned')),
    engagementDelta: periodDelta(sumField(recent, 'engagements'), sumField(prior, 'engagements')),
    usersDelta:
      period === 'daily'
        ? { pct: 1.2, direction: 'up' }
        : period === 'weekly'
          ? { pct: 3.4, direction: 'up' }
          : { pct: 8.1, direction: 'up' },
    listingsDelta: periodDelta(sumField(recent, 'listingsNew'), sumField(prior, 'listingsNew')),
  };
}

export function getMockEngagementMix(): EngagementMixItem[] {
  return [
    { key: 'view', label: 'Views', count: 48_200 },
    { key: 'like', label: 'Likes', count: 12_640 },
    { key: 'save', label: 'Saves', count: 8_320 },
    { key: 'comment', label: 'Comments', count: 3_180 },
    { key: 'preview', label: 'Previews', count: 6_540 },
    { key: 'favorite', label: 'Favorites', count: 2_910 },
    { key: 'review', label: 'Reviews', count: 1_420 },
    { key: 'feedback', label: 'Feedback', count: 890 },
  ];
}

export function getMockListingMix(): MixItem[] {
  return [
    { key: 'electronics', label: 'Electronics', count: 1_240 },
    { key: 'vehicles', label: 'Vehicles', count: 860 },
    { key: 'real_estate', label: 'Real estate', count: 640 },
    { key: 'fashion', label: 'Fashion', count: 520 },
    { key: 'home', label: 'Home & garden', count: 410 },
    { key: 'services', label: 'Services', count: 380 },
    { key: 'jobs', label: 'Jobs', count: 290 },
    { key: 'other', label: 'Other', count: 180 },
  ];
}

const MIN_DELTA_BASE = 10;

export function periodDelta(
  current: number,
  previous: number,
): { pct: number; direction: 'up' | 'down' | 'flat' } {
  // A percentage off a handful of events is noise (1 -> 3,580 listings read as
  // +357,900%), so a small base reports no change at all.
  if (previous < MIN_DELTA_BASE) return { pct: 0, direction: 'flat' };
  const pct = Math.round(((current - previous) / previous) * 1000) / 10;
  if (Math.abs(pct) < 0.05) return { pct: 0, direction: 'flat' };
  return { pct, direction: pct > 0 ? 'up' : 'down' };
}

export function sumField(
  points: TrendPoint[],
  field: keyof Pick<
    TrendPoint,
    | 'acquired'
    | 'signups'
    | 'logins'
    | 'failedLogins'
    | 'sessions'
    | 'churned'
    | 'engagements'
    | 'listings'
    | 'listingsNew'
    | 'dau'
  >,
): number {
  return points.reduce((s, p) => s + p[field], 0);
}

export function rangesEqual(a: DateRange, b: DateRange): boolean {
  return toInputDate(a.from) === toInputDate(b.from) && toInputDate(a.to) === toInputDate(b.to);
}

export type TrendMode = 'period' | 'cumulative';

/**
 * Running totals for flow metrics; stock metrics stay as levels. Distinct
 * counts (dau, mau, visitors) cannot be summed, so their running total is the
 * first-seen series: `newActive` / `newVisitors` accumulate into unique reach.
 */
export function toCumulativeSeries(points: TrendPoint[]): TrendPoint[] {
  const totals: Partial<Record<(typeof FLOW_FIELDS)[number], number>> = {};
  return points.map((p) => {
    const next = { ...p };
    for (const f of FLOW_FIELDS) {
      totals[f] = (totals[f] ?? 0) + p[f];
      next[f] = totals[f]!;
    }
    return next;
  });
}
